import * as React from 'react'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { AccountService } from '@halo/services/account.service'
import { UserService } from '@halo/services/user.service'
import { AccountDto } from '@halo/dtos/account.dto'
import { UserDto } from '@halo/dtos/user.dto'
import { classnames } from '@halo/_utils/classnames'
import { UserDetailsWell } from './user-details-well'
import { UserAccessWell } from './user-access-well'
import { UserActivityLogWell } from './user-activity-log-well'
import './style/user-data-form.scss'
import { IApiResponse } from '@pushly/aqe/lib/interfaces'
import { AppState } from '@halo/stores/app.state'
import AccessControlService from '@halo/services/access-control.service'
import { asCaslSubject, CurrentUserCan } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { IUserPermissionsResponse } from '@halo/interfaces/user-permissions'

interface IUserDataForm {
    userId: number
    accountId?: number
}

interface IUDFState {
    loading: boolean
    user?: UserDto
    account?: AccountDto
    actingUserAccessRecords?: IUserPermissionsResponse
    requestedUserAccessRecords?: IUserPermissionsResponse
}

export class UserDataForm extends React.Component<IUserDataForm, IUDFState> {
    protected appState: AppState
    protected appService: AppService
    protected accountService: AccountService
    protected userService: UserService

    public constructor(props: IUserDataForm) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.accountService = Container.get(AccountService)
        this.userService = Container.get(UserService)

        this.state = {
            loading: true,
        }
    }

    public componentDidMount() {
        if (this.props.userId) {
            this.fetchRequestedUser()
        }
    }

    public render() {
        const isSelf = this.appState.currentUser?.id === this.props.userId
        const shouldShowActivityLogWell =
            this.appState.currentUser?.isInternalUser &&
            this.appService.currentRoute.includes('organizations') &&
            this.props.accountId

        return (
            <div className={classnames('user-data-form')}>
                <UserDetailsWell
                    loading={!this.state.user}
                    user={this.state.user}
                    accountId={this.props.accountId}
                    caslUser={this.requestedUserCaslSubject}
                    onUpdated={this.handleUserUpdated}
                />

                {!isSelf && this.state.user && (
                    <CurrentUserCan do={AbilityAction.UPDATE} on={this.requestedUserCaslSubject}>
                        <UserAccessWell
                            loading={!this.state.user}
                            user={this.state.user}
                            accountId={this.props.accountId}
                        />
                    </CurrentUserCan>
                )}

                {shouldShowActivityLogWell && (
                    <CurrentUserCan do={AbilityAction.UPDATE} on={this.requestedUserCaslSubject}>
                        <UserActivityLogWell
                            loading={!this.state.user}
                            user={this.state.user}
                            accountId={this.props.accountId}
                        />
                    </CurrentUserCan>
                )}
            </div>
        )
    }

    protected get requestedUserCaslSubject() {
        let accountId = this.props.accountId
        if (!accountId) {
            const actingUserAcctIds = this.appState.abilityStore.acs.accountIdsWhereIsAdmin
            const requestedUserAcctIds = this.state.requestedUserAccessRecords?.accounts?.map((a) => a.account_id) ?? []
            accountId = actingUserAcctIds.find((id) => requestedUserAcctIds.includes(id))
        }

        return asCaslSubject(SubjectEntity.USER, {
            id: this.props.userId,
            accountId,
            userTypeId: this.state.user?.user_type_id,
        })
    }

    protected handleUserUpdated = async (updatedUser: UserDto) => {
        this.setState(({ user }) => ({
            user: UserDto.fromApiResponse({
                ...user,
                ...updatedUser,
            }),
        }))
    }

    protected async fetchRequestedUser() {
        if (!this.props.userId) {
            setTimeout(() => this.fetchRequestedUser(), 50)
            return
        }

        this.setState({ loading: true })

        const update: any = { loading: false }
        update.actingUserAccessRecords = await this.appState.abilityStore.acs.getRawPermissionRecords()

        const options: any = { showLoadingScreen: false }
        let response: IApiResponse<UserDto> = { ok: false, data: undefined }

        if (this.props.accountId) {
            const calls: any[] = []

            calls.push(this.userService.fetchById(this.props.userId, options))

            calls.push(await this.accountService.fetchById(this.props.accountId, {}, false))

            const callData = await Promise.all(calls)
            response = callData[0]

            const account = callData[1]
            if (account) {
                update.account = account
            }
        } else {
            response = await this.userService.fetchById(this.props.userId, options)
        }

        if (response.ok) {
            update.user = response.data

            const acs = AccessControlService.forUser(update.user)
            update.requestedUserAccessRecords = await acs.getRawPermissionRecords(true)
        }

        this.setState(update)
    }
}
